import { useEffect } from 'react';

/**
 * Bottom sheet (docs/DESIGN-SYSTEM.md §7.6): grabber, thumb-zone commit,
 * Escape/scrim dismissal. All Phase-1 tasks are ≤2 steps, so one detent
 * suffices for now.
 */
export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      <section
        style={{
          position: 'relative', background: 'var(--color-bg-elevated)',
          borderRadius: 'var(--radius-sheet) var(--radius-sheet) 0 0',
          padding: 'var(--space-3) var(--space-4) calc(var(--space-6) + env(safe-area-inset-bottom))',
          maxHeight: '85dvh', overflowY: 'auto', maxWidth: 720, width: '100%', margin: '0 auto',
        }}
      >
        <div aria-hidden="true" style={{ width: 36, height: 5, borderRadius: 3, background: 'var(--color-separator)', margin: '0 auto var(--space-4)' }} />
        <h2 style={{ font: 'var(--type-title2)', margin: '0 0 var(--space-4)' }}>{title}</h2>
        {children}
      </section>
    </div>
  );
}

export const fieldStyle: React.CSSProperties = {
  width: '100%', minHeight: 50, padding: '0 var(--space-4)',
  borderRadius: 'var(--radius-control)', border: 'none',
  background: 'var(--color-fill-subtle)', marginBottom: 'var(--space-3)',
};

export function PrimaryButton({ className, style, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  // Delegates to the component layer; width stays full because the sheet's
  // commit button lives in the thumb zone and must be unmissable.
  return (
    <button
      {...props}
      className={`btn btn-primary${className ? ` ${className}` : ''}`}
      style={{ width: '100%', minHeight: 50, ...style }}
    />
  );
}
